import useAddProspectToStrategy from 'hooks/services/strategy/useAddProspectToStrategy';
import React, { useState } from 'react';
import { Prospect } from 'crono-fe-common/types/prospect';
import { ResumeSequenceInstanceWrapper } from './style';
import {
  CircularProgress,
  ClickAwayListener,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import StrategyCard from '../strategyCard';
import { MainButton } from 'crono-fe-common/components/CronoButton';
import StrategySelector from '../strategySelector';
import { colors } from 'crono-fe-common/theme';
import { useConditionalSnackBar } from 'context/snackbar';
import { getError, isProspectOwned } from 'crono-fe-common/utils';
import { useAnalytics } from 'context/analytics';
import useGetStrategy, {
  useInvalidateGetStrategy,
} from 'hooks/services/strategy/useGetStrategy';
import PlusSmallIcon from 'crono-fe-common/icons/Icon-Plus-Small';
import { StrategyCopyConstants } from 'crono-fe-common/constants/copyConstants/strategyCopyConstants';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import useNonOwnedSequenceActionGuard from 'hooks/strategy/useNonOwnedSequenceActionGuard';

interface IProps {
  prospect: Prospect;
  setOpenTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  addToStrategyOrientation: 'left' | 'right';
}

export const CustomSecondaryTooltipRight = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip
      arrow={props.arrow ? props.arrow : false}
      {...props}
      classes={{ popper: className }}
    />
  ),
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    position: 'absolute',
    bottom: -17,
    left: 104,
    maxWidth: 450,
    border: `1px solid ${colors.grey4}`,
    borderRadius: 16,
    padding: 0,
    WebkitBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    MozBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    OBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    BoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
  },
}));

export const CustomSecondaryTooltipLeft = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip
      arrow={props.arrow ? props.arrow : false}
      {...props}
      classes={{ popper: className }}
    />
  ),
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    position: 'absolute',
    bottom: -17,
    right: 240,
    maxWidth: 450,
    border: `1px solid ${colors.grey4}`,
    borderRadius: 16,
    padding: 0,
    WebkitBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    MozBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    OBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    BoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
  },
}));

const ResumeSequenceInstance = ({
  prospect,
  setOpenTooltip,
  addToStrategyOrientation,
}: IProps) => {
  const analytics = useAnalytics();
  const { isManager } = useRoleCapabilities();
  const { openNonOwnedSequenceActionModal } = useNonOwnedSequenceActionGuard();

  const invalidateGetStrategy = useInvalidateGetStrategy();

  const { data: strategyResponse } = useGetStrategy(
    prospect.sequenceInstance?.strategyId,
  );
  const strategy = strategyResponse?.data?.data;
  const {
    mutate: addProspectToStrategy,
    error: addProspectToStrategyError,
    isLoading: isAddProspectToStrategyLoading,
  } = useAddProspectToStrategy({
    onSuccess: (result, variables) => {
      setOpenTooltip(false);
      invalidateGetStrategy({
        strategyId: variables.data.strategyId,
      });
    },
  });

  useConditionalSnackBar([
    {
      condition: !!addProspectToStrategyError,
      message:
        getError(addProspectToStrategyError) ?? 'Error adding to sequence',
      severity: 'error',
    },
  ]);

  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const handleResume = () => {
    const sequenceInstance = prospect.sequenceInstance;
    if (!sequenceInstance) return;

    const resumeSequence = () => {
      addProspectToStrategy({
        data: {
          accountId: prospect.accountId,
          prospectId: prospect.objectId,
          strategyId: sequenceInstance.strategyId,
          resume: true,
        },
        recipient: prospect,
        strategySettings: strategy?.strategySettings ?? null,
      });
      if (analytics) {
        analytics.track('add-to-sequence', {
          type: 'resume',
        });
      }
    };

    if (isManager && !isProspectOwned(prospect)) {
      openNonOwnedSequenceActionModal('resume', resumeSequence);
    } else {
      resumeSequence();
    }
  };

  return (
    <ResumeSequenceInstanceWrapper>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        fontSize={14}
        lineHeight={'22px'}
      >
        Do you want to resume the sequence?
      </Typography>
      {prospect.sequenceInstance && strategy && (
        <div className="strategy-to-resume">
          <StrategyCard strategy={strategy} />
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
        }}
      >
        {isAddProspectToStrategyLoading ? (
          <CircularProgress />
        ) : (
          <MainButton onClick={handleResume} height={'32px'} fontSize={'14px'}>
            Resume
          </MainButton>
        )}

        <ClickAwayListener onClickAway={handleTooltipClose}>
          <div>
            {addToStrategyOrientation === 'left' ? (
              <CustomSecondaryTooltipLeft
                title={
                  <StrategySelector
                    prospect={prospect}
                    setOpenTooltip={setOpen}
                    callOnSuccess={() => {
                      setOpen(false);
                      setOpenTooltip(false);
                    }}
                  />
                }
                onClose={handleTooltipClose}
                open={open}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <div className="base-button" onClick={handleTooltipOpen}>
                  <div className="div-add-staretgy-plus">
                    <PlusSmallIcon
                      color={colors.white}
                      className="add-strategy-plus"
                    />
                  </div>
                  {StrategyCopyConstants.ADD_TO_STRATEGY_TEXT}
                </div>
              </CustomSecondaryTooltipLeft>
            ) : (
              <CustomSecondaryTooltipRight
                title={
                  <StrategySelector
                    prospect={prospect}
                    setOpenTooltip={setOpen}
                    callOnSuccess={() => {
                      setOpen(false);
                      setOpenTooltip(false);
                    }}
                  />
                }
                onClose={handleTooltipClose}
                open={open}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <div className="base-button" onClick={handleTooltipOpen}>
                  <div className="div-add-staretgy-plus">
                    <PlusSmallIcon
                      color={colors.white}
                      className="add-strategy-plus"
                    />
                  </div>
                  {StrategyCopyConstants.ADD_TO_STRATEGY_TEXT}
                </div>
              </CustomSecondaryTooltipRight>
            )}
          </div>
        </ClickAwayListener>
      </div>
    </ResumeSequenceInstanceWrapper>
  );
};

export default ResumeSequenceInstance;
